import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import IMAGES from "@/data/images";
import { User, Clock, Wrench, MapPin } from "lucide-react";

const engagements = [
  {
    icon: User,
    title: "Un seul interlocuteur",
    text: "Du devis à la réception des travaux, vous avez un contact unique qui coordonne l'ensemble du chantier.",
  },
  {
    icon: Clock,
    title: "15 ans d'expérience",
    text: "Entreprise fondée en 2011, nous intervenons depuis plus de 15 ans sur des chantiers de rénovation parisiens.",
  },
  {
    icon: Wrench,
    title: "Tous corps d'état",
    text: "Maçonnerie, électricité, plomberie, carrelage, menuiserie, peinture : nous couvrons l'ensemble des métiers du bâtiment.",
  },
  {
    icon: MapPin,
    title: "Paris & Ile-de-France",
    text: "Notre zone d'intervention couvre tout le Grand Paris et la région Ile-de-France.",
  },
];

const realisationsApercu = [
  {
    key: "salle-de-bain-carrelage-chevron",
    legende: "Salle de bain — Paris",
  },
  {
    key: "verriere-atelier-bureaux",
    legende: "Bureaux professionnels — Paris",
  },
  {
    key: "creation-mezzanine-escalier-bois",
    legende: "Mezzanine sur mesure — Paris",
  },
];

export default function Accueil() {
  return (
    <>
      <SEO
        title="Bati Rui — Artisan rénovation tous corps d'état Paris 13 | Ile-de-France"
        description="Bati Rui, entreprise de rénovation tous corps d'état à Paris depuis 2011. Electricité, plomberie, carrelage, menuiserie, peinture. Devis gratuit sous 48h."
        canonical="https://www.batirui.com/"
      />

      {/* Hero */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center"
      >
        <img
          src={IMAGES["renovation-entree-escalier-helicoidal"].url}
          alt={IMAGES["renovation-entree-escalier-helicoidal"].alt}
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-[#1B2E4B]/60" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h1
            data-testid="hero-h1"
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight leading-tight"
          >
            Bati Rui — Artisan tous corps d'état à Paris depuis 2011
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-white/90 leading-relaxed max-w-2xl mx-auto">
            Rénovation complète, aménagement sur mesure, finitions soignées.
            Un seul interlocuteur pour tous vos travaux à Paris et en Ile-de-France.
          </p>
          <Link
            to="/contact"
            data-testid="hero-cta"
            className="inline-block mt-10 bg-[#C0392B] hover:bg-[#a02f23] text-white rounded-md px-8 py-4 text-base font-medium transition-colors"
          >
            Demander un devis gratuit
          </Link>
        </div>
      </section>

      {/* Qui sommes-nous */}
      <section data-testid="about-section" className="py-20 md:py-32 bg-[#F8F6F2]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1B2E4B] tracking-tight">
            Une entreprise parisienne depuis 2011
          </h2>
          <div className="mt-8 space-y-5 text-[#3D3D3A] text-base sm:text-lg leading-relaxed">
            <p>
              Fondée en 2011 par Rui Mariano Rodrigues, Bati Rui est une entreprise
              artisanale implantée dans le 13ème arrondissement de Paris. Depuis plus
              de 15 ans, nous accompagnons particuliers et professionnels dans leurs
              projets de rénovation, d'aménagement et de transformation.
            </p>
            <p>
              Notre atout principal : intervenir sur tous les corps de métier du bâtiment
              avec une seule équipe, pour une coordination sans faille et des délais maîtrisés.
            </p>
          </div>
        </div>
      </section>

      {/* Engagements */}
      <section data-testid="engagements-section" className="py-20 md:py-32 bg-[#EEECE8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1B2E4B] tracking-tight text-center mb-16">
            Nos engagements
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-12">
            {engagements.map((item) => (
              <div
                key={item.title}
                data-testid={`engagement-${item.title.toLowerCase().replace(/\s/g, "-")}`}
                className="bg-[#F8F6F2] rounded-md p-8"
              >
                <item.icon size={32} className="text-[#C0392B] mb-4" />
                <h3 className="font-heading text-xl font-semibold text-[#1B2E4B] mb-3">
                  {item.title}
                </h3>
                <p className="text-[#3D3D3A] text-sm sm:text-base leading-relaxed">
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Aperçu réalisations */}
      <section data-testid="realisations-preview" className="py-20 md:py-32 bg-[#F8F6F2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1B2E4B] tracking-tight text-center mb-16">
            Quelques réalisations
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {realisationsApercu.map((item) => (
              <div key={item.key} className="group">
                <div className="overflow-hidden rounded-md">
                  <img
                    src={IMAGES[item.key].url}
                    alt={IMAGES[item.key].alt}
                    className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <p className="mt-3 text-sm font-medium text-[#3D3D3A]">
                  {item.legende}
                </p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/realisations"
              data-testid="link-voir-realisations"
              className="text-[#C0392B] hover:text-[#a02f23] font-medium text-sm transition-colors"
            >
              Voir toutes nos réalisations &rarr;
            </Link>
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section data-testid="cta-final" className="py-20 md:py-32 bg-[#1B2E4B]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white tracking-tight">
            Vous avez un projet de rénovation ?
          </h2>
          <p className="mt-6 text-white/80 text-base sm:text-lg leading-relaxed">
            Nous nous déplaçons pour un rendez-vous de chiffrage sans engagement
            et vous répondons sous 48h.
          </p>
          <Link
            to="/contact"
            data-testid="cta-final-devis"
            className="inline-block mt-10 bg-[#C0392B] hover:bg-[#a02f23] text-white rounded-md px-8 py-4 text-base font-medium transition-colors"
          >
            Demander un devis gratuit
          </Link>
        </div>
      </section>
    </>
  );
}
